<template>
  <div class="admin-offices">
    <admin-view-header title="Biura OTvK">
      <div class="buttons">
        <button class="button is-danger is-outlined" @click="resetForm">
          <icon name="fa-trash" />
          <span>Wyczyść</span>
        </button>
        <button class="button is-primary is-outlined" @click="showPreview">
          <icon name="fa-eye" />
          <span>Podgląd</span>
        </button>
        <protected :with="Permission.CREATE_TEMPLATE">
          <button
            class="button is-primary"
            @click="submit"
            :class="{ 'is-loading': submitting }"
            :disabled="submitting"
          >
            <icon name="fa-check" />
            <span>Zapisz</span>
          </button>
        </protected>
      </div>
    </admin-view-header>

    <loader :state="actions.templates">
      <div>
        <transition-group name="fade" class="offices" tag="div">
          <div
            class="office box"
            v-for="(office, officeIndex) in fields[T.Contact.OFFICES]"
            :key="office.id"
          >
            <div class="office__item office__name">{{ office.name }}</div>
            <div class="office__item office__address">
              <icon name="fa-map-marker-alt" />
              <div>
                <p>{{ office.address }}</p>
                <p>{{ office.city }}</p>
              </div>
            </div>
            <div class="office__item office__days">
              <icon name="fa-calendar-alt" />
              <p>{{ office.days }}</p>
            </div>
            <div class="office__item office__hours">
              <icon name="fa-clock" />
              <div>
                <p>{{ office.hours }}</p>
                <p>{{ office.disclaimer }}</p>
              </div>
            </div>
            <div class="office__item office__phones">
              <div>
                <p
                  v-for="(phone, phoneIndex) in office.phones"
                  :key="phoneIndex"
                >
                  <icon name="fa-phone-alt" />
                  <span>{{ phone }}</span>
                </p>
              </div>
            </div>
            <div class="office__item office__mails">
              <div>
                <p v-for="(mail, mailIndex) in office.mails" :key="mailIndex">
                  <icon name="fa-at" />
                  <span>{{ mail }}</span>
                </p>
              </div>
            </div>
            <div class="office__map">
              <geo-map
                class="map"
                :location="{ ...office, desc: office.address }"
                :zoom="map.options.zoom"
                :max-zoom="map.options.maxZoom"
              />
            </div>
            <protected :with="Permission.CREATE_TEMPLATE">
              <div class="office__item office__controls">
                <icon
                  class="office-control office-control--remove"
                  name="fa-trash"
                  data-tooltip="Usuń biuro"
                  @click="removeOffice(officeIndex)"
                />
                <icon
                  class="office-control"
                  name="fa-arrow-left"
                  data-tooltip="Przesuń w lewo"
                  @click="moveOfficeToLeft(officeIndex)"
                  v-if="officeIndex > 0"
                />
                <icon
                  class="office-control"
                  name="fa-arrow-right"
                  data-tooltip="Przesuń w prawo"
                  @click="moveOfficeToRight(officeIndex)"
                  v-if="officeIndex < fields[T.Contact.OFFICES].length - 1"
                />
                <icon
                  class="office-control"
                  name="fa-pen"
                  data-tooltip="Edytuj biuro"
                  @click="editOffice(officeIndex)"
                />
              </div>
            </protected>
          </div>
          <protected :with="Permission.CREATE_TEMPLATE" key="add-office">
            <div class="add-office">
              <icon name="fa-plus-circle" @click="addOffice" />
            </div>
          </protected>
        </transition-group>

        <div class="footer field">
          <label class="label">{{ FIELDS[T.Contact.FOOTER] }}</label>
          <editor
            frame
            placeholder="Opcjonalny podpis..."
            v-model="fields[T.Contact.FOOTER]"
            :readonly="!hasPermission(Permission.CREATE_TEMPLATE)"
          />
        </div>
      </div>

      <template #loading>
        <div
          class="spinner is-3by4 is-grey-lighter is-thick is-large is-centered"
        ></div>
      </template>

      <template #error>
        <div class="grey-message">
          <icon name="fa-times-circle" />
          <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
        </div>
      </template>
    </loader>
  </div>
</template>

<script>
import {
  Protected,
  WithTemplates,
  WithUnsavedChangesConfirmation,
  WithPreview,
  WithReset
} from "%mixins";

import Editor from "%admin/components/global/Editor";

import { Icon, GeoMap } from "%helpers";
import AdminViewHeader from "%admin/components/global/AdminViewHeader";

import { OfficeModal } from "%modals";

import T from "%const/templates";

const FIELDS = {
  [T.Contact.OFFICES]: "Biura/Punkty obsługi",
  [T.Contact.FOOTER]: "Podpis"
};

export default {
  name: "admin-offices",

  components: {
    Icon,
    AdminViewHeader,
    Editor,
    GeoMap
  },

  mixins: [
    Protected,

    WithTemplates({
      templates: [T.Contact.OFFICES, T.Contact.FOOTER],
      json: [T.Contact.OFFICES]
    }),

    WithUnsavedChangesConfirmation({
      checkChanges() {
        return (
          this.templatesLoaded() && this.areAnyTemplateChanges(this.fields)
        );
      },

      async onSave() {
        return await this.submit();
      }
    }),

    WithPreview({
      path: "/home",
      query: {
        step: "contact"
      }
    }),

    WithReset(function() {
      this.initData();
    })
  ],

  data() {
    return {
      T,
      FIELDS,

      map: {
        options: {
          zoom: 19,
          maxZoom: 19
        }
      },

      fields: {
        [T.Contact.OFFICES]: [],
        [T.Contact.FOOTER]: ""
      },

      submitting: false
    };
  },

  async mounted() {
    await this.requestTemplates();
    this.initData();
  },

  methods: {
    async submit() {
      return await this.updateChangedTemplates(
        this.fields,
        FIELDS,
        () => (this.submitting = true),
        () => (this.submitting = false)
      );
    },

    initData() {
      this.applyTemplates(this.fields, {
        [T.Contact.OFFICES]: offices =>
          offices.map(office => ({ ...office, id: Symbol() }))
      });
    },

    addOffice() {
      this.$emit("show-modal", {
        component: OfficeModal,
        props: {
          onSave: office =>
            this.fields[T.Contact.OFFICES].push({ id: Symbol(), ...office })
        }
      });
    },

    editOffice(index) {
      this.$emit("show-modal", {
        component: OfficeModal,
        props: {
          value: this.fields[T.Contact.OFFICES][index],
          onSave: office =>
            this.fields[T.Contact.OFFICES].splice(index, 1, office),
          edit: true
        }
      });
    },

    moveOfficeToLeft(index) {
      if (index > 0) {
        this.swapOffices(index, index - 1);
      }
    },

    moveOfficeToRight(index) {
      if (index < this.fields[T.Contact.OFFICES].length - 1) {
        this.swapOffices(index, index + 1);
      }
    },

    swapOffices(a, b) {
      const tmp = this.fields[T.Contact.OFFICES][a];
      this.fields[T.Contact.OFFICES].splice(
        a,
        1,
        this.fields[T.Contact.OFFICES][b]
      );
      this.fields[T.Contact.OFFICES].splice(b, 1, tmp);
    },

    removeOffice(index) {
      this.fields[T.Contact.OFFICES].splice(index, 1);
    }
  }
};
</script>

<style lang="scss" scoped>
.offices {
  display: flex;
  flex-flow: row wrap;
  width: 100%;

  .office {
    margin: 0.3rem;
    min-width: 20rem;
    max-width: 25rem;
    display: flex;
    flex-flow: column nowrap;
    justify-content: space-between;
    transition: 0.1s;

    &__item {
      margin: 0.3rem;
      display: flex;

      .icon {
        margin-right: 0.5rem;
      }
    }

    &__name {
      font-weight: 700;
    }

    &__map {
      .map {
        height: 10rem;
        z-index: 0;
      }
    }

    &__controls {
      margin-top: 1rem;
      display: flex;
      justify-content: space-between;

      .office-control {
        color: $grey-lighter;
        cursor: pointer;

        &:not(:last-child) {
          margin-right: 1rem;
        }

        &:hover {
          color: $grey-light;
        }

        &--remove:hover {
          color: $danger;
        }
      }
    }
  }

  .add-office {
    margin: 0.3rem;
    width: 10rem;
    color: $grey-lighter;
    font-size: 5rem;
    display: flex;
    justify-content: center;
    align-items: center;

    .icon {
      cursor: pointer;
      &:hover {
        color: $grey;
      }
    }
  }
}

.footer {
  margin-top: 3rem;
}
</style>
